import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function GetCity() {
  const [cityList, setCityList] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 30;

  useEffect(() => {
    fetch('https://visitguru.in/api/v1/city/getCity')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setCityList(data.data);
        }
      })
      .catch(error => {
        console.error('Error fetching city list:', error);
        toast.error('Failed to load cities');
      });
  }, []);

  const handleDelete = async (cityId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#11ADA3',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch('https://visitguru.in/api/v1/city/deleteCity', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _id: cityId }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success('City deleted successfully');
        setCityList(prev => prev.filter(city => city._id !== cityId));
      } else {
        toast.error(data.message || 'Failed to delete city');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Something went wrong');
    }
  };

  const handleDownload = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('City List', 14, 22);

    const tableColumn = ["#", "City Name"];
    const tableRows = [];

    cityList.forEach((city, index) => {
      const row = [(index + 1).toString(), city.cityName];
      tableRows.push(row);
    });

    doc.autoTable({
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [8, 132, 196] },
    });

    doc.save('city-list.pdf');
  };

  const filteredCityList = cityList.filter(city =>
    city?.cityName?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const totalPages = Math.ceil(filteredCityList.length / limit);
  const currentData = filteredCityList.slice((page - 1) * limit, page * limit);

  const handlePrev = () => setPage(prev => Math.max(prev - 1, 1));
  const handleNext = () => setPage(prev => Math.min(prev + 1, totalPages));

  return (
    <div className="space-y-12">
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        {/* Header */}
        <div className="rounded-t px-4 py-3 border-0 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h3 className="font-semibold text-base text-blueGray-700" style={{ marginRight: "20px" }}>
              Cities/Villages <span className="text-sm text-gray-600">(Total: {filteredCityList.length})</span>
            </h3>

            {/* Search Bar */}
            <input
              type="text"
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setPage(1); // reset to page 1 when searching
              }}
              placeholder="Search City..."
              className="px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: "250px", borderRadius: "10px" }}
            />
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            style={{
              backgroundColor: "#0884c4",
              width: "100px",
              height: "35px",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              outline: "none",
            }}
          >
            Download
          </button>
        </div>



        {/* Table */}
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left">
                  #
                </th>
                <th className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left">
                  City/Village Name
                </th>
                <th className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((city, index) => (
                  <tr key={city._id}>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {city.cityName}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDelete(city._id)}
                          className="p-2 rounded-full hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200 transition"
                          aria-label="Delete city"
                        >
                          <i className="fas fa-trash-alt text-red-500 text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center p-4">
                    No cities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredCityList.length > limit && (
          <div className="flex justify-between items-center px-6 py-4">
            <button
              onClick={handlePrev}
              disabled={page === 1}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={page === totalPages}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GetCity;
